/**
 * Video catalog — the 10 talks currently in Prof. Nabil Tawfik's YouTube
 * playlist (https://www.youtube.com/playlist?list=PL1h2j-S1k8B2EJLMEdJ_1nMiQl7jtK6lg).
 * `youtubeId` enables inline embedding; the Videos page falls back to a
 * playlist-link placeholder for any entry that lacks one.
 */

export interface VideoEntry {
  id: number;
  slug: string;
  language: "ar" | "en";
  youtubeId?: string;
  /** Optional SRT file in public/transcripts/, displayed via transcript toggle. */
  transcriptFile?: string;
  /**
   * YouTube provides three alternative auto-picked thumbnail frames per video
   * at /vi/<id>/{1|2|3}.jpg (and /mq{1|2|3}.jpg for the 320x180 versions).
   * Set this when YouTube's default frame is unflattering (e.g., mid-blink,
   * mouth open). Default behaviour uses maxresdefault → mqdefault.
   */
  thumbnailFrame?: 1 | 2 | 3;
  date?: string;
  ar: { t: string; d: string };
  en: { t: string; d: string };
}

export const YOUTUBE_PLAYLIST_ID = "PL1h2j-S1k8B2EJLMEdJ_1nMiQl7jtK6lg";
export const YOUTUBE_PLAYLIST_URL = `https://www.youtube.com/playlist?list=${YOUTUBE_PLAYLIST_ID}`;
export const YOUTUBE_PLAYLIST_EMBED = `https://www.youtube.com/embed/videoseries?list=${YOUTUBE_PLAYLIST_ID}`;

export const VIDEOS: VideoEntry[] = [
  {
    id: 1,
    slug: "nile-cruise-2015-07-05",
    language: "en",
    youtubeId: "uVOB-eQ2VR8",
    date: "2015-07-05",
    ar: {
      t: "محاضرة رحلة النيل — ٧ / ٥ / ٢٠١٥",
      d: "لقاء تلفزيوني باللغة الإنجليزية على قناة Nile TV يتناول أسرار الحضارة المصرية القديمة وإيمان المصريين القدماء بالتوحيد.",
    },
    en: {
      t: "Nile Cruise Lecture — 7 May 2015",
      d: "English-language appearance on Nile TV's Nile Cruise programme discussing the mysteries of ancient Egyptian civilization and the monotheistic faith of its people.",
    },
  },
  {
    id: 2,
    slug: "nile-cruise-2015-11-12",
    language: "en",
    youtubeId: "jNPvLJvm4e4",
    date: "2015-11-12",
    ar: {
      t: "محاضرة رحلة النيل — ١٢ / ١١ / ٢٠١٥",
      d: "الحلقة الثانية من سلسلة Nile Cruise، تتطرق إلى الاكتشافات الحديثة حول الأهرامات والتوحيد في مصر القديمة.",
    },
    en: {
      t: "Nile Cruise Lecture — 12 November 2015",
      d: "Second Nile Cruise appearance, touching on recent pyramid discoveries and monotheism in ancient Egypt.",
    },
  },
  {
    id: 3,
    slug: "masriyat-tawheed-1",
    language: "ar",
    youtubeId: "wrOGkyZTIlo",
    date: "2015-09-14",
    ar: {
      t: "برنامج مصريات — حقيقة التوحيد في مصر القديمة (الجزء الأول)",
      d: "حلقة من برنامج مصريات تتناول حقيقة العقيدة في مصر القديمة وأن المصريين القدماء هم أول أمم الأرض التي آمنت بوحدانية الله تعالى.",
    },
    en: {
      t: "Masriyat — The Truth of Monotheism in Ancient Egypt (Part 1)",
      d: "TV programme (Arabic). Discusses the true ancient Egyptian creed and that the ancient Egyptians were the first people to believe in the Oneness of God.",
    },
  },
  {
    id: 4,
    slug: "masriyat-tawheed-2",
    language: "ar",
    youtubeId: "MZ-WwgwMuUo",
    date: "2015-09-14",
    ar: {
      t: "برنامج مصريات — حقيقة التوحيد في مصر القديمة (الجزء الثاني)",
      d: "استكمال الحديث عن حقيقة العقيدة في مصر القديمة ونماذج من النصوص المصرية القديمة الدالة على التوحيد.",
    },
    en: {
      t: "Masriyat — The Truth of Monotheism in Ancient Egypt (Part 2)",
      d: "Continuing the discussion of the ancient Egyptian creed with examples from the original texts pointing to monotheism.",
    },
  },
  {
    id: 5,
    slug: "masriyat-tawheed-3",
    language: "ar",
    youtubeId: "f-_O2OJGXGM",
    date: "2015-09-14",
    ar: {
      t: "برنامج مصريات — حقيقة التوحيد في مصر القديمة (الجزء الثالث)",
      d: "الجزء الختامي من السلسلة، ويتناول الشواهد الأثرية والنصوص الدالة على إيمان المصريين القدماء بالله الواحد.",
    },
    en: {
      t: "Masriyat — The Truth of Monotheism in Ancient Egypt (Part 3)",
      d: "Final part of the series, covering the archaeological and textual evidence for the ancient Egyptians' belief in One God.",
    },
  },
  {
    id: 6,
    slug: "belad-teeba-2015-03-16",
    language: "ar",
    youtubeId: "HwgJmXMA5E4",
    date: "2015-03-16",
    ar: {
      t: "بلاد طيبة مع هبة شامل — ١٦ مارس ٢٠١٥",
      d: "حوار تلفزيوني مع الإعلامية هبة شامل على قناة بلاد طيبة، إخراج نورهان البيلي.",
    },
    en: {
      t: "Belad Teeba with Heba Shamel — 16 March 2015",
      d: "TV interview with Heba Shamel on Belad Teeba, directed by Nourhan El Beily.",
    },
  },
  {
    id: 7,
    slug: "cairo-archaeology-seminar",
    language: "ar",
    youtubeId: "-PGypomZ_Fw",
    date: "2015-09-18",
    ar: {
      t: "جزء من ندوة نبيل توفيق بكلية الآثار — جامعة القاهرة",
      d: "مقتطف من ندوة أقامها نبيل توفيق بقاعة مؤتمرات كلية الآثار بجامعة القاهرة عن الحضارة المصرية القديمة ورؤية عقلية موثقة لها.",
    },
    en: {
      t: "Excerpt from Nabil Tawfik's seminar at the Faculty of Archaeology, Cairo University",
      d: "Excerpt from Nabil Tawfik's seminar in the conference hall of the Faculty of Archaeology at Cairo University, presenting a reasoned, documented reading of ancient Egyptian civilization.",
    },
  },
  {
    id: 8,
    slug: "fi-daerat-eldaw",
    language: "ar",
    youtubeId: "txYtDjIQ-HU",
    date: "2015-09-11",
    ar: {
      t: "في دائرة الضوء — لقاء مع الأستاذ نبيل توفيق",
      d: "حوار تلفزيوني مع الأستاذ نبيل توفيق، المتخصص في الحضارة المصرية القديمة.",
    },
    en: {
      t: "Fi Daerat El-Daw — Interview with Prof. Nabil Tawfik",
      d: "TV interview with Prof. Nabil Tawfik, scholar of ancient Egyptian civilization.",
    },
  },
  {
    id: 9,
    slug: "misr-men-tany-2",
    language: "ar",
    youtubeId: "UMKg6jZhHqM",
    date: "2015-09-11",
    ar: {
      t: "نبيل توفيق في برنامج «مصر من تاني» — الحلقة الثانية",
      d: "الحلقة الثانية من برنامج «مصر من تاني» مع الإعلامي إسلام العتيق، تستمر في تبيين أهمية توضيح حقيقة العقيدة في مصر القديمة.",
    },
    en: {
      t: "Nabil Tawfik on Misr Men Tany — Episode 2",
      d: "Second episode of Misr Men Tany with Islam El-Ateeq, continuing the discussion on the importance of clarifying the true ancient Egyptian creed.",
    },
  },
  {
    id: 10,
    slug: "nabil-tawfek-eps01",
    language: "ar",
    youtubeId: "cvAvypUO0NM",
    // Default maxresdefault frame was unflattering; frame 2 (mid-video) is cleaner.
    thumbnailFrame: 2,
    date: "2015-09-10",
    ar: {
      t: "نبيل توفيق — الحلقة الأولى",
      d: "الحلقة الأولى من سلسلة لنبيل توفيق حول حقيقة العقيدة المصرية القديمة.",
    },
    en: {
      t: "Nabil Tawfik — Episode 1",
      d: "First episode of a Nabil Tawfik series on the true ancient Egyptian creed.",
    },
  },
];
